import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { addDays, subDays } from "date-fns";

import { Chart } from "@/components/chart";
import { useResource } from "@/hooks/resources";

type WordsLearnedChartProps = {
  childId?: string | number;
  demo?: boolean;
};

const shortDateFormatter = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
});

const formatWeek = (iso: string) => {
  const start = new Date(iso + "T00:00:00");
  const end = addDays(start, 6);
  return `${shortDateFormatter.format(start)} – ${shortDateFormatter.format(end)}`;
};

export const WordsLearnedChart = ({ childId, demo = false }: WordsLearnedChartProps) => {
  const { t } = useTranslation("charts");

  const [wordsLearnedRaw] = useResource<Array<{ week: string, wordsLearned: number, totalWordsLearned: number }>>(
    `/reports/learned_words_per_week?child_id=${childId}`,
    { enabled: !demo },
  )

  const wordsLearned = useMemo(() =>
    (wordsLearnedRaw || []).map(entry => ({
      ...entry,
      week: formatWeek(entry.week),
    })),
    [wordsLearnedRaw]
  );

  let totalWordsLearned = 0;
  const demoWordsLearned = [0, 3, 5, 0, 8, 6, 0, 7, 8, 0, 4, 2].map((wordsLearned, index) => {
    const start = subDays(new Date(), 7 * (12 - index));
    return {
    week: `${shortDateFormatter.format(start)} – ${shortDateFormatter.format(addDays(start, 6))}`,
    wordsLearned,
    totalWordsLearned: totalWordsLearned += wordsLearned
  };})

  return (
    <Chart
      options={{
        tooltip: {
          trigger: "axis",
          valueFormatter: (value) => t("wordsLearned.words", { count: Number(value) }),
        },
        xAxis: { type: "category" },
        yAxis: [{ name: "Weekly Words Learned" }, { name: "All Time Words Learned" }],
        series: [{ type: "bar", name: "Weekly", yAxisIndex: 0 }, { type: "line", name: "All Time", yAxisIndex: 1, smooth: true }],
        dataset: {
          dimensions: ["week", "wordsLearned", "totalWordsLearned"],
          source: demo ? demoWordsLearned : (wordsLearned || [])
        }
      }}
      className="h-[400px]"
    />
  )
}
